import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, LoginRequest, RegisterRequest } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore user from token on page refresh
    const token = authService.getToken();
    if (token) {
      try {
        // Decode JWT token to get user info (without verification for client-side)
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Decoded token payload:', payload); // Debug log
        setUser({
          userId: payload.userId || payload.sub,
          username: payload.upn || payload.sub || '',
          email: payload.email || '',
          role: Array.isArray(payload.groups) ? payload.groups[0] : payload.role || 'USER',
        });
      } catch (error) {
        console.error('Failed to decode token:', error);
        // Token is invalid, remove it
        authService.logout();
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);
    // Use user info from login response instead of calling /auth/me
    setUser({
      userId: response.userId,
      username: response.username,
      email: response.email,
      role: response.role,
    });
  };

  const register = async (userData: RegisterRequest) => {
    const response = await authService.register(userData);
    // Use user info from register response instead of calling /auth/me
    setUser({
      userId: response.userId,
      username: response.username,
      email: response.email,
      role: response.role,
    });
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user || !!authService.getToken(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

